import { DataTablePrimeReact } from "@finanedi.es/storybook"
import { useConciliacionBancariaId } from "api/ConciliacionBancaria.hook"
import { deleteConciliacionBancariaById } from "api/ConciliacionBancaria.service"
import { useCuentaBancariaTesoreriaById } from "api/CuentaBancaria.hook"
import {
  getMovimientosByCuentaBancaria,
  IbanMovimientosParamsProps,
} from "api/CuentaBancaria.service"
import { CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO } from "api/DTOs/ConciliacionConciliarMovimientosCommandDto"
import { MovimientoBancarioReadingDtoProps } from "api/DTOs/MovimientoBancarioReadingDto"
import { getFlujoTesoreriaConciliablesByAsientoContableId } from "api/FlujoTesoreria.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import { createLineaDefault } from "features/Contabilidad/components/ModalAsientoContable/constant"
import ModalAsientoContable from "features/Contabilidad/components/ModalAsientoContable/ModalAsientoContable"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import { ModalDocumentosDesconciliar } from "../BancosDetail/components/ModalDocumentosDesconciliar"
import { ModalNota } from "../BancosDetail/components/ModalNota"
import ModalForzarConciliacion from "./components/ModalForzarConciliacion"
import ModalRestaurarConciliacion from "./components/ModalRestaurarConciliacion"
import NoteButton from "./components/NoteButton"
import "./conciliaciones.css"
import { calculateImportePendiente } from "./constants/constants"
import { useManualRowSelection } from "./hooks/useManualRowSelection"
import LeftColumn from "./layout/ColumnLeft"
import RightColumn from "./layout/ColumnRight"
import Header from "./layout/Header"

export interface SaldosTeoricos {
  saldoTeoricoBancario: number
  saldoTeoricoContable: number
  diferencia: number
}

function Conciliaciones() {
  const { t } = useTranslation()
  const { id: paramId = "" } = useParams<Record<"id", string>>()

  const [date, setDate] = useState<[Date, Date | null]>([
    new Date(new Date().getFullYear(), 0, 1),
    new Date(),
  ])

  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [saldosTeoricos, setSaldosTeoricos] = useState<SaldosTeoricos>({
    saldoTeoricoBancario: 0,
    saldoTeoricoContable: 0,
    diferencia: 0,
  })

  const dtRef = useRef<DataTablePrimeReact<
    MovimientoBancarioReadingDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const { cuentaBancariaById } = useCuentaBancariaTesoreriaById(paramId)

  const fetchMovimientos = useCallback(
    () => (queryParams: IbanMovimientosParamsProps) => {
      if (cuentaBancariaById?.iban) {
        return getMovimientosByCuentaBancaria(
          {
            ...queryParams,
            SortingCriteria: queryParams.SortingCriteria,
            SkipCount: queryParams?.SkipCount ?? 0,
            MaxResultCount: queryParams?.MaxResultCount ?? 10,
          },
          cuentaBancariaById?.iban
        )
      }
      return null
    },
    [cuentaBancariaById?.iban]
  )

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchMovimientos,
    })

  const {
    showDesconciliarModalData,
    handleCloseCreateModal,
    handleConfirmManualConciliacion,
    handleCreateNota,
    handleCreateNotaModal,
    handleUnselectRows,
    isUpperSectionExpanded,
    isUpperSectionExpandedSecond,
    setShowDesconciliarModalData,
    setShowModalAsiento,
    setShowModalMarcarConciliado,
    setShowModalRestaurarConciliacion,
    showCreateNotaModal,
    showModalAsiento,
    showModalMarcarConciliado,
    showModalRestaurarConciliacion,
    toggleUpperSection,
    toggleUpperSectionSecond,
    upperSectionForzada,
    selectedNewLeft,
    setSelectedNewLeft,
    selectedNewRight,
    setSelectedNewRight,
  } = useManualRowSelection({ t, fetchTableData, paramId })

  const [refrescar, setRefrescar] = useState(0)

  const refrescarTablas = () => {
    handleResetPagination()
    setRefrescar((prev) => prev + 1)
  }

  const { conciliacionBancariaById } = useConciliacionBancariaId(
    selectedNewLeft?.[0]?.conciliacionBancariaId
  )

  const handleDeleteConciliacion = async () => {
    const resp = await deleteConciliacionBancariaById(
      selectedNewLeft?.[0]?.conciliacionBancariaId as string
    )
    if (resp.status === STATUSCORRECT) {
      fetchTableData()
      handleUnselectRows()
      setShowDesconciliarModalData(false)
    }
  }

  const OptionsButton = (row: MovimientoBancarioReadingDtoProps) => {
    return (
      <NoteButton
        hasNote={!!row?.nota}
        openNotaModal={() => handleCreateNotaModal(row)}
      />
    )
  }

  const saldoPendiente: number = calculateImportePendiente(
    selectedNewLeft,
    selectedNewRight
  )

  return (
    <div className='tw-flex lg:tw-h-[calc(100dvh-70px)] tw-flex-col'>
      <Header
        date={date}
        setDate={setDate}
        refrescarTablas={refrescarTablas}
        saldosTeoricos={saldosTeoricos}
        setSaldosTeoricos={setSaldosTeoricos}
      />
      <div className='tw-flex tw-flex-1 tw-overflow-auto lg:tw-overflow-hidden'>
        <div className='tw-grid tw-flex-1 tw-grid-cols-1 tw-pt-1 lg:tw-grid-cols-2 tw-grid-rows-1'>
          <LeftColumn
            selectedNewLeft={selectedNewLeft}
            setSelectedNewLeft={setSelectedNewLeft}
            handleResetPagination={handleResetPagination}
            isLoading={isLoading}
            items={items}
            OptionsButton={OptionsButton}
            pagination={pagination}
            dtRef={dtRef}
            upperSectionForzada={upperSectionForzada}
            setPagination={setPagination}
            setServiceQuery={setServiceQuery}
            setSortingCriteria={setSortingCriteria}
            sortingCriteria={sortingCriteria}
            t={t}
            totalCount={totalCount}
            datePadre={date}
            refrescar={refrescar}
          />
          <RightColumn
            conciliacionBancariaById={conciliacionBancariaById}
            isUpperSectionExpanded={isUpperSectionExpanded}
            isUpperSectionExpandedSecond={isUpperSectionExpandedSecond}
            toggleUpperSection={toggleUpperSection}
            toggleUpperSectionSecond={toggleUpperSectionSecond}
            conciliarMovimientoData={selectedNewLeft}
            conciliarMovimientoStatus={!!selectedNewLeft?.length}
            t={t}
            handleConfirmManualConciliacion={handleConfirmManualConciliacion}
            handleForzarConciliacion={() => {
              setShowModalMarcarConciliado(!showModalMarcarConciliado)
            }}
            handleRestaurarConciliacion={() => {
              setShowModalRestaurarConciliacion(!showModalRestaurarConciliacion)
            }}
            handleCrearAsiento={() => {
              setShowModalAsiento(!showModalAsiento)
            }}
            handleDeleteConciliacion={() => {
              setShowDesconciliarModalData(true)
            }}
            datePadre={date}
            refrescar={refrescar}
            selectedNewRight={selectedNewRight}
            setSelectedNewRight={setSelectedNewRight}
          />
        </div>
      </div>

      <ModalDocumentosDesconciliar
        isOpen={showDesconciliarModalData}
        onCancel={() =>
          setShowDesconciliarModalData(!showDesconciliarModalData)
        }
        onConfirm={() => {
          handleDeleteConciliacion()
        }}
      />

      <ModalRestaurarConciliacion
        showModalRestaurarConciliacion={showModalRestaurarConciliacion}
        setShowModalRestaurarConciliacion={setShowModalRestaurarConciliacion}
        data={selectedNewLeft?.[0]}
        fetchTableData={fetchTableData}
        handleUnselectRows={handleUnselectRows}
      />

      <ModalForzarConciliacion
        showModalMarcarConciliado={showModalMarcarConciliado}
        setShowModalMarcarConciliado={setShowModalMarcarConciliado}
        data={selectedNewLeft}
        dataMovContables={selectedNewRight}
        handleUnselectRows={handleUnselectRows}
        fetchTableData={refrescarTablas}
      />

      <ModalNota
        isOpen={showCreateNotaModal.status}
        onHide={handleCloseCreateModal}
        data={showCreateNotaModal.data}
        handleSubmitAnnotation={handleCreateNota}
      />

      <ModalAsientoContable
        idToEdit={false}
        show={showModalAsiento}
        setShow={setShowModalAsiento}
        updateTable={async (responseData: string | undefined) => {
          await fetchTableData()
          refrescarTablas()
          if (!responseData) return

          const addAsiento =
            await getFlujoTesoreriaConciliablesByAsientoContableId(
              responseData,
              {
                CuentaBancariaId: cuentaBancariaById?.id,
              }
            )
          if (!addAsiento.items?.[0]) return
          setSelectedNewRight((prev) => [
            ...prev,
            {
              tabKey:
                CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO.FLUJO_TESORERIA_IDS,
              ...addAsiento?.items?.[0],
            },
          ])
        }}
        preData={{
          fecha: new Date(selectedNewLeft?.[0]?.fecha ?? Date.now()),
          lineas: [
            {
              id: uuidv4(),
              cuentaContableId:
                cuentaBancariaById?.cuentaContableEmpresaId ?? undefined,
              numeroDocumento: "",
              descripcion: selectedNewLeft?.[0]?.concepto ?? "",
              listadoTags: [],
              debe: saldoPendiente > 0 ? Math.abs(saldoPendiente) : null,
              haber: saldoPendiente < 0 ? Math.abs(saldoPendiente) : null,
            },
            createLineaDefault(),
            createLineaDefault(),
          ],
        }}
      />
    </div>
  )
}

export default Conciliaciones
